import React, { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import orderService from '../services/orderService'
import { joinRestaurantOrders } from '../services/orderWebSocket'
import {
  RestaurantOrder,
  RestaurantOrderStatus,
  ORDER_STATUS_LABELS,
  ORDER_STATUS_BADGE,
} from '../types/order'
import { useTranslation } from '../hooks/use-i18n'

const RestaurantOrders: React.FC = () => {
  const { t } = useTranslation()
  const { restaurantId } = useParams<{ restaurantId: string }>()
  const STATUS_FILTERS: { value: RestaurantOrderStatus | 'all'; label: string }[] = [
    { value: 'all', label: t('order.filter.all') },
    { value: 'open', label: t('order.filter.open') },
    { value: 'submitted', label: t('order.filter.submitted') },
    { value: 'served', label: t('order.filter.served') },
    { value: 'cancelled', label: t('order.filter.cancelled') },
  ]

  const navigate = useNavigate()
  const [orders, setOrders] = useState<RestaurantOrder[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState<RestaurantOrderStatus | 'all'>('all')
  const [creating, setCreating] = useState(false)

  const load = useCallback(async () => {
    if (!restaurantId) return
    try {
      setIsLoading(true)
      const data = await orderService.listOrders(
        restaurantId,
        filter === 'all' ? undefined : filter,
      )
      setOrders(data)
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('error.failed'))
    } finally {
      setIsLoading(false)
    }
  }, [restaurantId, filter])

  useEffect(() => {
    load()
    if (!restaurantId) return
    return joinRestaurantOrders(restaurantId, () => load())
  }, [load, restaurantId])

  const handleNewOrder = async () => {
    if (!restaurantId) return
    try {
      setCreating(true)
      const order = await orderService.createOrder(restaurantId, {})
      navigate(`/restaurant/${restaurantId}/orders/${order._id}`)
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.createOrderFailed'))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div className="min-h-screen">
      <main className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="mb-6">
          <Link
            to={`/restaurant/${restaurantId}`}
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            {t('common.backToRestaurant')}
          </Link>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h1 className="text-2xl sm:text-3xl font-bold text-white">{t('order.title')}</h1>
            <button
              type="button"
              onClick={handleNewOrder}
              disabled={creating}
              className="px-5 py-2.5 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white rounded-xl font-semibold transition-all duration-300 disabled:opacity-50"
            >
              {creating ? t('order.creating') : t('order.newOrder')}
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                filter === f.value
                  ? 'bg-sky-500 text-white'
                  : 'bg-white/10 text-white/70 hover:bg-white/20'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="bg-[#2a2a2a] border border-white/10 rounded-3xl p-10 text-center">
            <div className="text-4xl mb-3">🧾</div>
            <h2 className="text-xl font-bold text-white mb-2">{t('order.empty.title')}</h2>
            <p className="text-white/60">{t('order.empty.body')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => (
              <Link
                key={order._id}
                to={`/restaurant/${restaurantId}/orders/${order._id}`}
                className="block bg-[#2a2a2a] border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-all"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold">
                        {order.tableLabel ? t('order.list.table', { label: order.tableLabel }) : t('order.list.order')}
                      </p>
                      <span
                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${ORDER_STATUS_BADGE[order.status]}`}
                      >
                        {ORDER_STATUS_LABELS[order.status]}
                      </span>
                    </div>
                    <p className="text-white/50 text-sm mt-1">
                      {t('order.list.meta', {
                        count: String(order.itemCount),
                        itemLabel: order.itemCount === 1 ? t('common.item') : t('common.items'),
                        staffName: order.createdBy?.name || t('common.fallbackStaff'),
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-white font-bold text-lg">${order.total.toFixed(2)}</p>
                    <p className="text-white/40 text-xs">
                      {new Date(order.createdAt).toLocaleString()}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}

export default RestaurantOrders
